import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import api from '@/services/api';
import { useToast } from "@/hooks/use-toast";
import { Building2, Save, Settings, ChevronDown, ChevronUp, Star, Bot } from "lucide-react";
import { analysesService } from "@/services/analyses.service";

interface Societe {
  id: string;
  nom: string;
}

interface SocieteNote {
  id?: string;
  ao_id: string;
  societe_id: string;
  note_prix: number | null;
  note_durabilite: number | null;
  note_qualite_offre: number | null;
  note_references: number | null;
  poids_prix: number | null;
  poids_durabilite: number | null;
  poids_qualite_offre: number | null;
  poids_references: number | null;
  commentaire: string | null;
}

interface AOSocieteNotesManagerProps {
  aoId: string;
}

export const AOSocieteNotesManager = ({ aoId }: AOSocieteNotesManagerProps) => {
  const { toast } = useToast();
  const [societes, setSocietes] = useState<Societe[]>([]);
  const [notes, setNotes] = useState<Record<string, SocieteNote>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);
  const [weightsOpen, setWeightsOpen] = useState(false);
  const [iaScores, setIaScores] = useState<Record<string, number | null>>({});

  // Global weights
  const [globalWeights, setGlobalWeights] = useState({
    poids_prix: 20,
    poids_durabilite: 20,
    poids_qualite_offre: 20,
    poids_qualite_offre_ia: 20,
    poids_references: 20
  });

  useEffect(() => {
    loadSocietes();
    loadNotes();
    loadIaScores();
  }, [aoId]);

  const loadSocietes = async () => {
    try {
      // Get unique societes from depots for this AO via API
      const { data: response } = await api.get(`/appels-offres/${aoId}/societes`);
      const societesData = response?.data || [];
      setSocietes(societesData.sort((a: Societe, b: Societe) => a.nom.localeCompare(b.nom)));
    } catch (error) {
      console.error('Erreur chargement sociétés:', error);
      toast({
        title: "Erreur",
        description: "Impossible de charger les sociétés",
        variant: "destructive"
      });
    }
  };

  const loadNotes = async () => {
    try {
      const { data: response } = await api.get(`/appels-offres/${aoId}/societe-notes`);
      const notesData = response?.data || [];

      const notesMap: Record<string, SocieteNote> = {};
      notesData.forEach((note: any) => {
        notesMap[note.societeId] = {
          id: note.id,
          ao_id: note.aoId,
          societe_id: note.societeId,
          note_prix: note.notePrix,
          note_durabilite: note.noteDurabilite,
          note_qualite_offre: note.noteQualiteOffre,
          note_references: note.noteReferences,
          poids_prix: note.poidsPrix,
          poids_durabilite: note.poidsDurabilite,
          poids_qualite_offre: note.poidsQualiteOffre,
          poids_references: note.poidsReferences,
          commentaire: note.commentaire
        };
        // Set global weights from first note that has them
        if (note.poidsPrix !== null && globalWeights.poids_prix === 20) {
          setGlobalWeights({
            poids_prix: note.poidsPrix,
            poids_durabilite: note.poidsDurabilite || 20,
            poids_qualite_offre: note.poidsQualiteOffre || 20,
            poids_qualite_offre_ia: note.poidsQualiteOffreIa || 20,
            poids_references: note.poidsReferences || 20
          });
        }
      });
      setNotes(notesMap);
    } catch (error) {
      console.error('Erreur chargement notes:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadIaScores = async () => {
    try {
      const analysesData = await analysesService.getByAoId(aoId);
      // Group by société and calculate average score
      const scoresBySociete: Record<string, number[]> = {};
      analysesData?.forEach((analyse: any) => {
        const depot = analyse.depot;
        const candidat = depot?.candidat;
        const societe = candidat?.societe;
        if (!societe || analyse.overallScore == null) return;
        if (!scoresBySociete[societe.id]) {
          scoresBySociete[societe.id] = [];
        }
        scoresBySociete[societe.id].push(analyse.overallScore);
      });
      // Convert averages from 0-100 to 0-5 scale
      const scores: Record<string, number | null> = {};
      Object.entries(scoresBySociete).forEach(([societeId, societeScores]) => {
        const avg = societeScores.reduce((sum, s) => sum + s, 0) / societeScores.length;
        scores[societeId] = Math.round((avg / 100) * 5 * 100) / 100;
      });
      setIaScores(scores);
    } catch (error) {
      console.error('Erreur chargement scores IA:', error);
    }
  };

  const handleNoteChange = (societeId: string, field: keyof SocieteNote, value: number | string | null) => {
    setNotes(prev => ({
      ...prev,
      [societeId]: {
        ...prev[societeId] || { ao_id: aoId, societe_id: societeId, note_prix: null, note_durabilite: null, note_qualite_offre: null, note_references: null, poids_prix: null, poids_durabilite: null, poids_qualite_offre: null, poids_references: null, commentaire: null },
        [field]: value
      }
    }));
  };

  const handleGlobalWeightChange = (field: 'poids_prix' | 'poids_durabilite' | 'poids_qualite_offre' | 'poids_qualite_offre_ia' | 'poids_references', value: number) => {
    // Simply update the field value - no automatic redistribution
    setGlobalWeights({
      ...globalWeights,
      [field]: value
    });
  };

  const applyGlobalWeights = () => {
    const updatedNotes = { ...notes };
    societes.forEach(societe => {
      if (updatedNotes[societe.id]) {
        updatedNotes[societe.id] = {
          ...updatedNotes[societe.id],
          poids_prix: globalWeights.poids_prix,
          poids_durabilite: globalWeights.poids_durabilite,
          poids_qualite_offre: globalWeights.poids_qualite_offre,
          poids_references: globalWeights.poids_references
        };
      }
    });
    setNotes(updatedNotes);
    toast({
      title: "Pondérations appliquées",
      description: "Les pondérations globales ont été appliquées à toutes les sociétés"
    });
  };

  const saveNote = async (societeId: string) => {
    setSaving(societeId);
    try {
      const note = notes[societeId];
      if (!note) return;

      const noteData = {
        societeId: societeId,
        notePrix: note.note_prix,
        noteDurabilite: note.note_durabilite,
        noteQualiteOffre: note.note_qualite_offre,
        noteReferences: note.note_references,
        poidsPrix: globalWeights.poids_prix,
        poidsDurabilite: globalWeights.poids_durabilite,
        poidsQualiteOffre: globalWeights.poids_qualite_offre,
        poidsQualiteOffreIa: globalWeights.poids_qualite_offre_ia,
        poidsReferences: globalWeights.poids_references,
        commentaire: note.commentaire
      };

      if (note.id) {
        await api.put(`/appels-offres/${aoId}/societe-notes/${note.id}`, noteData);
      } else {
        const { data: response } = await api.post(`/appels-offres/${aoId}/societe-notes`, noteData);
        setNotes(prev => ({
          ...prev,
          [societeId]: { ...note, id: response.data.id }
        }));
      }

      toast({
        title: "Sauvegardé",
        description: "Les notes ont été enregistrées"
      });
    } catch (error) {
      console.error('Erreur sauvegarde:', error);
      toast({
        title: "Erreur",
        description: "Impossible de sauvegarder les notes",
        variant: "destructive"
      });
    } finally {
      setSaving(null);
    }
  };

  const calculateWeightedScore = (societeId: string): number | null => {
    const note = notes[societeId];
    const iaScore = iaScores[societeId];
    if (!note && iaScore == null) return null;

    const { note_prix, note_durabilite, note_qualite_offre, note_references } = note || { note_prix: null, note_durabilite: null, note_qualite_offre: null, note_references: null };
    if (note_prix === null && note_durabilite === null && note_qualite_offre === null && note_references === null && iaScore == null) return null;

    const prix = note_prix || 0;
    const durabilite = note_durabilite || 0;
    const qualite = note_qualite_offre || 0;
    const qualiteIa = iaScore || 0;
    const references = note_references || 0;

    const score = (
      (qualiteIa * globalWeights.poids_qualite_offre_ia / 100) +
      (qualite * globalWeights.poids_qualite_offre / 100) +
      (prix * globalWeights.poids_prix / 100) +
      (durabilite * globalWeights.poids_durabilite / 100) +
      (references * globalWeights.poids_references / 100)
    );

    return Math.round(score * 100) / 100;
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Chargement...</p>
        </CardContent>
      </Card>
    );
  }

  if (societes.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5" />
            Analyse AO Société
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">
            <Building2 className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">Aucune société</h3>
            <p className="text-muted-foreground">
              Importez des CV pour voir les sociétés associées
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Global Weights Configuration */}
      <Card>
        <Collapsible open={weightsOpen} onOpenChange={setWeightsOpen}>
          <CollapsibleTrigger asChild>
            <CardHeader className="cursor-pointer hover:bg-accent/50 transition-colors">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Settings className="h-5 w-5" />
                  <CardTitle>Configuration des pondérations</CardTitle>
                </div>
                {weightsOpen ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
              </div>
              <CardDescription>
                Définissez les poids relatifs des critères Qualité de l'offre, Qualité IA, Prix, Durabilité et Références
              </CardDescription>
            </CardHeader>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <CardContent className="space-y-6">
              <div className="grid gap-6">
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <Label>Qualité de l'offre</Label>
                    <span className="text-sm text-muted-foreground">{globalWeights.poids_qualite_offre.toFixed(1)}%</span>
                  </div>
                  <Slider
                    value={[globalWeights.poids_qualite_offre]}
                    onValueChange={([value]) => handleGlobalWeightChange('poids_qualite_offre', value)}
                    max={100}
                    step={0.1}
                    className="w-full"
                  />
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <Label className="flex items-center gap-1.5">
                      <Bot className="h-4 w-4 text-purple-500" />
                      Qualité de l'offre - IA
                    </Label>
                    <span className="text-sm text-purple-500 font-medium">{globalWeights.poids_qualite_offre_ia.toFixed(1)}%</span>
                  </div>
                  <Slider
                    value={[globalWeights.poids_qualite_offre_ia]}
                    onValueChange={([value]) => handleGlobalWeightChange('poids_qualite_offre_ia', value)}
                    max={100}
                    step={0.1}
                    className="w-full"
                  />
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <Label>Prix</Label>
                    <span className="text-sm text-muted-foreground">{globalWeights.poids_prix.toFixed(1)}%</span>
                  </div>
                  <Slider
                    value={[globalWeights.poids_prix]}
                    onValueChange={([value]) => handleGlobalWeightChange('poids_prix', value)}
                    max={100}
                    step={0.1}
                    className="w-full"
                  />
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <Label>Durabilité</Label>
                    <span className="text-sm text-muted-foreground">{globalWeights.poids_durabilite.toFixed(1)}%</span>
                  </div>
                  <Slider
                    value={[globalWeights.poids_durabilite]}
                    onValueChange={([value]) => handleGlobalWeightChange('poids_durabilite', value)}
                    max={100}
                    step={0.1}
                    className="w-full"
                  />
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <Label>Références</Label>
                    <span className="text-sm text-muted-foreground">{globalWeights.poids_references.toFixed(1)}%</span>
                  </div>
                  <Slider
                    value={[globalWeights.poids_references]}
                    onValueChange={([value]) => handleGlobalWeightChange('poids_references', value)}
                    max={100}
                    step={0.1}
                    className="w-full"
                  />
                </div>
              </div>
              <div className="flex justify-between items-center pt-4 border-t">
                <p className="text-sm text-muted-foreground">
                  Total: {(globalWeights.poids_prix + globalWeights.poids_durabilite + globalWeights.poids_qualite_offre + globalWeights.poids_qualite_offre_ia + globalWeights.poids_references).toFixed(1)}%
                </p>
                <Button onClick={applyGlobalWeights} variant="outline">
                  Appliquer à toutes les sociétés
                </Button>
              </div>
            </CardContent>
          </CollapsibleContent>
        </Collapsible>
      </Card>

      {/* Company Notes */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5" />
            Notes par société ({societes.length})
          </CardTitle>
          <CardDescription>
            Évaluez chaque société sur les critères Qualité de l'offre, Qualité IA, Prix, Durabilité et Références (note de 0 à 5)
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {[...societes].sort((a, b) => {
              const scoreA = calculateWeightedScore(a.id) ?? -1;
              const scoreB = calculateWeightedScore(b.id) ?? -1;
              return scoreB - scoreA;
            }).map(societe => {
              const note = notes[societe.id] || { note_prix: null, note_durabilite: null, note_qualite_offre: null, note_references: null, commentaire: null };
              const weightedScore = calculateWeightedScore(societe.id);

              return (
                <Card key={societe.id} className="p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 space-y-4">
                      <div className="flex items-center justify-between">
                        <h3 className="font-semibold text-lg">{societe.nom}</h3>
                        {weightedScore !== null && (
                          <div className="flex items-center gap-2 bg-destructive/10 px-3 py-1 rounded-full">
                            <Star className="h-5 w-5 text-warning fill-warning" />
                            <span className="font-bold text-lg text-destructive">{weightedScore}/5</span>
                          </div>
                        )}
                      </div>
                      
                      <div className="grid grid-cols-5 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor={`qualite-${societe.id}`} className="flex items-center gap-2">
                            Qualité de l'offre
                            <div className="flex items-center gap-1">
                              <Input
                                type="number"
                                min="0"
                                max="100"
                                step="1"
                                className="h-6 w-14 text-xs px-1 text-center bg-warning/20 border-warning/30"
                                value={globalWeights.poids_qualite_offre.toFixed(0)}
                                onChange={(e) => handleGlobalWeightChange('poids_qualite_offre', Math.min(100, Math.max(0, parseFloat(e.target.value) || 0)))}
                              />
                              <span className="text-xs text-muted-foreground">%</span>
                            </div>
                          </Label>
                          <Input
                            id={`qualite-${societe.id}`}
                            type="number"
                            min="0"
                            max="5"
                            step="0.5"
                            placeholder="0-5"
                            value={note.note_qualite_offre ?? ''}
                            onChange={(e) => handleNoteChange(societe.id, 'note_qualite_offre', e.target.value ? parseFloat(e.target.value) : null)}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="flex items-center gap-2">
                            <Bot className="h-4 w-4 text-purple-500" />
                            Qualité IA
                            <div className="flex items-center gap-1">
                              <Input
                                type="number"
                                min="0"
                                max="100"
                                step="1"
                                className="h-6 w-14 text-xs px-1 text-center bg-purple-100 border-purple-300"
                                value={globalWeights.poids_qualite_offre_ia.toFixed(0)}
                                onChange={(e) => handleGlobalWeightChange('poids_qualite_offre_ia', Math.min(100, Math.max(0, parseFloat(e.target.value) || 0)))}
                              />
                              <span className="text-xs text-muted-foreground">%</span>
                            </div>
                          </Label>
                          <Input
                            readOnly
                            className="bg-purple-50 border-purple-200 text-purple-700 font-medium cursor-default"
                            value={iaScores[societe.id] != null ? `${iaScores[societe.id]}/5` : 'N/A'}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor={`prix-${societe.id}`} className="flex items-center gap-2">
                            Prix
                            <div className="flex items-center gap-1">
                              <Input
                                type="number"
                                min="0"
                                max="100"
                                step="1"
                                className="h-6 w-14 text-xs px-1 text-center bg-warning/20 border-warning/30"
                                value={globalWeights.poids_prix.toFixed(0)}
                                onChange={(e) => handleGlobalWeightChange('poids_prix', Math.min(100, Math.max(0, parseFloat(e.target.value) || 0)))}
                              />
                              <span className="text-xs text-muted-foreground">%</span>
                            </div>
                          </Label>
                          <Input
                            id={`prix-${societe.id}`}
                            type="number"
                            min="0"
                            max="5"
                            step="0.5"
                            placeholder="0-5"
                            value={note.note_prix ?? ''}
                            onChange={(e) => handleNoteChange(societe.id, 'note_prix', e.target.value ? parseFloat(e.target.value) : null)}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor={`durabilite-${societe.id}`} className="flex items-center gap-2">
                            Durabilité
                            <div className="flex items-center gap-1">
                              <Input
                                type="number"
                                min="0"
                                max="100"
                                step="1"
                                className="h-6 w-14 text-xs px-1 text-center bg-warning/20 border-warning/30"
                                value={globalWeights.poids_durabilite.toFixed(0)}
                                onChange={(e) => handleGlobalWeightChange('poids_durabilite', Math.min(100, Math.max(0, parseFloat(e.target.value) || 0)))}
                              />
                              <span className="text-xs text-muted-foreground">%</span>
                            </div>
                          </Label>
                          <Input
                            id={`durabilite-${societe.id}`}
                            type="number"
                            min="0"
                            max="5"
                            step="0.5"
                            placeholder="0-5"
                            value={note.note_durabilite ?? ''}
                            onChange={(e) => handleNoteChange(societe.id, 'note_durabilite', e.target.value ? parseFloat(e.target.value) : null)}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor={`references-${societe.id}`} className="flex items-center gap-2">
                            Références
                            <div className="flex items-center gap-1">
                              <Input
                                type="number"
                                min="0"
                                max="100"
                                step="1"
                                className="h-6 w-14 text-xs px-1 text-center bg-warning/20 border-warning/30"
                                value={globalWeights.poids_references.toFixed(0)}
                                onChange={(e) => handleGlobalWeightChange('poids_references', Math.min(100, Math.max(0, parseFloat(e.target.value) || 0)))}
                              />
                              <span className="text-xs text-muted-foreground">%</span>
                            </div>
                          </Label>
                          <Input
                            id={`references-${societe.id}`}
                            type="number"
                            min="0"
                            max="5"
                            step="0.5"
                            placeholder="0-5"
                            value={note.note_references ?? ''}
                            onChange={(e) => handleNoteChange(societe.id, 'note_references', e.target.value ? parseFloat(e.target.value) : null)}
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor={`commentaire-${societe.id}`}>Commentaire</Label>
                        <Textarea
                          id={`commentaire-${societe.id}`}
                          placeholder="Ajouter un commentaire..."
                          value={note.commentaire || ''}
                          onChange={(e) => handleNoteChange(societe.id, 'commentaire', e.target.value || null)}
                        />
                      </div>
                    </div>
                    
                    <Button 
                      onClick={() => saveNote(societe.id)} 
                      disabled={saving === societe.id}
                      size="sm"
                    >
                      <Save className="h-4 w-4 mr-1" />
                      {saving === societe.id ? 'Sauvegarde...' : 'Sauvegarder'}
                    </Button>
                  </div>
                </Card>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
